// src/api/invoices.js
import axios from 'axios'

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '/neue/admin',
  headers: { 'Content-Type': 'application/json' },
})

// — attach token to every request
apiClient.interceptors.request.use(config => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
}, err => Promise.reject(err))

// — catch 401s and force relogin
apiClient.interceptors.response.use(
  resp => resp,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

/**
 * Fetch invoices.
 * - Backend should filter based on logged-in user or return all if admin.
 * - Accepts optional params (e.g. { page: 1, limit: 20 })
 */
export async function fetchInvoices(params = {}) {
  // filter_user_id can be passed to scope results to a specific user
  const res = await apiClient.get('/neue/admin/invoices/list', { params })
  return res.data.data // Expecting [{...}, {...}]
}

/**
 * Create a new invoice.
 * @param {Object} payload - Invoice creation data
 *   Example:
 *   {
 *     customer: { storeName, email, phone },
 *     billingDetails: {...},
 *     invoiceItems: [...],
 *     invoice_date: 'YYYY-MM-DD',
 *     reference_number: 'INV-123'
 *   }
 */
export async function createInvoice(payload) {
  const res = await apiClient.post('/neue/admin/invoices/create', payload)
  return res.data // Expecting created invoice object
}

/**
 * Mark an invoice as paid (override).
 * PATCH /neue/admin/invoices/:invoiceId/mark-paid
 */
export async function markInvoicePaid(invoiceId) {
  const res = await apiClient.patch(`/neue/admin/invoices/${invoiceId}/mark-paid`)
  return res.data // { success: true, rocky_marked_paid: "<ISO date>" }
}

/**
 * Remove the paid mark override from an invoice.
 * DELETE /neue/admin/invoices/:invoiceId/mark-paid
 */
export async function removeInvoicePaidMark(invoiceId) {
  const res = await apiClient.delete(`/neue/admin/invoices/${invoiceId}/mark-paid`)
  return res.data // { success: true, rocky_marked_paid: null }
}

/**
 * Delete an invoice (soft delete).
 * DELETE /neue/admin/invoices/:invoiceId
 */
export async function deleteInvoice(invoiceId) {
  const res = await apiClient.delete(`/neue/admin/invoices/${invoiceId}`)
  return res.data
}